"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractReconnectStateManager = void 0;
const p_limit_1 = __importDefault(require("p-limit"));
const types_1 = require("@refinitiv-data/types");
const logger_1 = require("../logger");
const detect_environment_1 = require("../util/detect-environment");
const time_1 = require("../util/time");
const connection_error_1 = require("./connection-error");
const recover_error_1 = require("./recover-error");
const state_manager_1 = require("./state-manager");
class AbstractReconnectStateManager extends state_manager_1.AbstractStateManager {
    constructor() {
        super(...arguments);
        this.log = logger_1.logger.getLogger('reconnect-manager');
        this.environment = (0, detect_environment_1.detectEnvironment)();
        this.reconnectLimit = (0, p_limit_1.default)(1);
        this.firstInitFailed = false;
        this.firstInitSuccess = false;
        this.reconnectFailed = false;
    }
    async open() {
        await this.reconnectLimit(async () => {
            if (!this.firstInitSuccess) {
                await this.openFirstTime();
            }
            if (this.state === types_1.State.Closed) {
                await this.reconnect();
            }
        });
        return this;
    }
    async close() {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        if (this.onCleanUp) {
            this.onCleanUp();
        }
        // To avoid situation, when cleanUp is not able to call an appropriate onCleanUp()
        this.reconnectFailed = true;
        return this.reconnectLimit(async () => {
            this.firstInitFailed = false;
            this.firstInitSuccess = false;
            this.reconnectFailed = false;
            await super.close();
        });
    }
    async closeWithKeptReconnect() {
        return super.close();
    }
    async reopen() {
        const intervals = [...types_1.StreamReconnectIntervals[this.environment]];
        await this.recover(intervals);
    }
    async openFirstTime() {
        if (this.firstInitFailed) {
            throw new connection_error_1.ConnectionError('The first initialization failed!');
        }
        try {
            await super.open();
            this.firstInitSuccess = true;
        }
        catch (err) {
            this.firstInitFailed = true;
            throw err;
        }
    }
    async reconnect() {
        if (this.reconnectFailed) {
            this.log.error('Recover has failed!');
            throw new recover_error_1.RecoverError('Recover has failed!');
        }
        try {
            await this.reopen();
            this.log.warn('Reopened successfully!');
        }
        catch (err) {
            this.reconnectFailed = true;
            this.log.error('Reconnect error', err);
            throw err;
        }
    }
    async recover(intervals) {
        this.log.debug(`Start recovering. Intervals:`, intervals);
        return new Promise(async (resolve, reject) => {
            this.onCleanUp = reject.bind(null, new Error('Initialization dropped due to close() action'));
            try {
                await super.open();
                resolve(void 0);
            }
            catch (err) {
                if (intervals.length === 0) {
                    reject(err);
                    return;
                }
                this.reconnectTimeout = setTimeout(() => {
                    // For Node.js it will be constant retry for the last interval until the session is closed
                    if (!(intervals.length === 1 && this.environment === detect_environment_1.Environment.NODEJS)) {
                        intervals.shift();
                    }
                    resolve(this.recover(intervals));
                }, intervals[0] * 1000);
                this.log.info(`Next reconnection attempt is in ${(0, time_1.formatTime)(intervals[0])}`);
            }
        });
    }
}
exports.AbstractReconnectStateManager = AbstractReconnectStateManager;
//# sourceMappingURL=data:application/json;base64,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